<template>
  <section>
    <div v-if="loading" class="fade my-6 border border-white border-opacity-20 rounded-md bg-sp-secondary bg-opacity-70"><Loading /></div>
    <div v-else-if="error">{{ $t("error") }}</div>
    <div 
      v-else 
      class="
        grid
        grid-cols-[repeat(auto-fit,minmax(120px,1fr))]
        gap-1
        justify-items-center
        max-w-screen-lg
        mx-auto
      "
    >
      <RouterLink
        v-for="(category, index) in categories"
        :key="category.id"
        :to="`/category/${category.id}`"
        @click="logClick(`main.${category.id}`, 'main.categories')"
        class="
          text-sp-primary
          w-full max-w-[clamp(120px,18vw,200px)]
          aspect-[2/3]
          relative
          rounded-md
          hover:text-sp-accent
          duration-150 ease-in-out
          lg:hover:drop-shadow-[0_0_4px_rgba(255,253,237,1)]
        "
      >
        <img
          :src="bgImages[index % bgImages.length].lowRes"
          :data-src="bgImages[index % bgImages.length].highRes"
          alt="Category"
          class="
            lazy-img 
            w-full 
            h-full 
            object-contain 
            rounded-md 
            blur-sm
          "
        />
        <div class="absolute inset-x-0 bottom-0 h-[38%] flex items-center justify-center px-2">
          <h2
            class="text-[clamp(0.75rem,2.2vw,1.125rem)] text-center leading-tight line-clamp-2 text-ellipsis"
          >
            {{ category.name || "Untitled Category" }}
          </h2>
        </div>
      </RouterLink>
    </div> 
  </section>
</template>

<script>
import { onMounted, nextTick } from "vue"
import { getUserData } from '../modules/collectData.js'
import { useLogger } from '../composables/useLogger'
import { useLazyImages } from '../composables/useLazyImages'
import { useHelpCenterFetch } from '../composables/useHelpCenterFetch'
import { CATEGORY_TILE_BACKGROUNDS } from '../config/endpoints'
import Loading from '../components/Loading.vue'

export default {
  components: {
    Loading
  },
  setup() {
    const { lang } = getUserData()
    const { logClick } = useLogger()

    const bgImages = CATEGORY_TILE_BACKGROUNDS

    const { data: categories, loading, error, load } = useHelpCenterFetch(
      () => `helpcenter/${lang}`,
      {
        select: (answer) => {
          if (!Array.isArray(answer)) throw new Error('expected array')
          return answer.map(item => ({
            name: item.title || 'Untitled Category',
            id: item.id || 'No id available'
          }))
        }
      }
    )

    const { observe: observeLazyImages } = useLazyImages({
      onIntersect: (img) => {
        const highResSrc = img.dataset.src
        if (!highResSrc) return
        img.src = highResSrc
        if (img.complete) {
          img.classList.remove('blur-sm')
        } else {
          img.onload = () => img.classList.remove('blur-sm')
        }
      }
    })

    const lazyLoadCategories = () => {
      observeLazyImages(document.querySelectorAll('.lazy-img'))
    }

    onMounted(async () => {
      await load()
      nextTick(() => lazyLoadCategories())
    })

    return {
      bgImages,
      categories,
      loading,
      error,
      logClick,
      lazyLoadCategories
    }
  }
}
</script>

<style>
.lazy-img {
  transition: filter 0.3s ease-out, opacity 0.3s ease-out;
}
</style>